import { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useHwcgMemberCompanies,
  useUpdateHwcgMemberCompany,
  type HwcgMemberCompany,
} from 'hooks/hwcgMemberCompanies/hwcgMemberCompanyHooks';

type LocationDraft = { eocLocation: string; officeLocation: string };

/**
 * Directory of HWCG member companies with EOC and office locations. Seeded on
 * first load; editable locations feed create-incident / Group Actions.
 */
export function HwcgMemberCompaniesTable() {
  const companies = useHwcgMemberCompanies();
  const update = useUpdateHwcgMemberCompany();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<LocationDraft>({
    eocLocation: '',
    officeLocation: '',
  });

  const beginEdit = (row: HwcgMemberCompany) => {
    setEditingId(row.id);
    setDraft({
      eocLocation: row.eocLocation,
      officeLocation: row.officeLocation,
    });
  };

  const cancel = () => {
    setEditingId(null);
    setDraft({ eocLocation: '', officeLocation: '' });
  };

  const save = (id: string) => {
    update.mutate(
      {
        id,
        eocLocation: draft.eocLocation,
        officeLocation: draft.officeLocation,
      },
      { onSuccess: () => cancel() }
    );
  };

  return (
    <section className="tw:flex tw:flex-col tw:gap-2">
      <div>
        <h2 className="tw:m-0 tw:text-sm tw:font-semibold">Member Companies</h2>
        <p className="tw:m-0 tw:mt-1 tw:text-xs tw:text-muted-foreground">
          EOC location fills Activate EOC; Office location fills Report-to when
          a Responsible Party is selected.
        </p>
      </div>
      <div className="tw:min-w-0 tw:overflow-hidden tw:rounded-lg tw:border-2 tw:border-solid tw:border-foreground/70 tw:bg-card">
        {companies.isLoading ? (
          <p className="tw:p-4 tw:text-sm tw:text-muted-foreground">
            Loading member companies…
          </p>
        ) : null}
        {companies.isError ? (
          <p className="tw:p-4 tw:text-sm tw:text-destructive">
            Member companies could not be loaded.
          </p>
        ) : null}
        {!companies.isLoading && !companies.isError ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member Company</TableHead>
                <TableHead>EOC Location</TableHead>
                <TableHead>Office Location</TableHead>
                <TableHead className="tw:w-24">
                  <span className="tw:sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(companies.data ?? []).length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="tw:text-muted-foreground"
                  >
                    No member companies yet.
                  </TableCell>
                </TableRow>
              ) : (
                (companies.data ?? []).map((row) =>
                  editingId === row.id ? (
                    <MemberCompanyEditorRow
                      key={row.id}
                      companyName={row.companyName}
                      draft={draft}
                      pending={update.isPending}
                      onChange={setDraft}
                      onCancel={cancel}
                      onSave={() => save(row.id)}
                    />
                  ) : (
                    <TableRow
                      key={row.id}
                      className="tw:cursor-pointer"
                      onClick={() => beginEdit(row)}
                    >
                      <TableCell className="tw:font-medium">
                        {row.companyName}
                      </TableCell>
                      <TableCell>
                        {row.eocLocation.trim() || (
                          <span className="tw:text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.officeLocation?.trim() || (
                          <span className="tw:text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            beginEdit(row);
                          }}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                )
              )}
            </TableBody>
          </Table>
        ) : null}
      </div>
    </section>
  );
}

function MemberCompanyEditorRow({
  companyName,
  draft,
  pending,
  onChange,
  onCancel,
  onSave,
}: {
  companyName: string;
  draft: LocationDraft;
  pending: boolean;
  onChange: (next: LocationDraft) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <TableRow>
      <TableCell className="tw:font-medium">{companyName}</TableCell>
      <TableCell>
        <Input
          aria-label={`EOC location for ${companyName}`}
          value={draft.eocLocation}
          autoFocus
          maxLength={500}
          onChange={(event) =>
            onChange({ ...draft, eocLocation: event.target.value })
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onSave();
            }
          }}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label={`Office location for ${companyName}`}
          value={draft.officeLocation}
          maxLength={500}
          onChange={(event) =>
            onChange({ ...draft, officeLocation: event.target.value })
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onSave();
            }
          }}
        />
      </TableCell>
      <TableCell>
        <div className="tw:flex tw:justify-end tw:gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Save locations"
            disabled={pending}
            onClick={onSave}
          >
            <Check className="tw:size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Cancel"
            disabled={pending}
            onClick={onCancel}
          >
            <X className="tw:size-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
